/**
 * WebhookList - List and manage event-notification webhooks
 *
 * Embedded inside the Settings → Webhooks tab. Lists an org's webhooks and
 * exposes create / edit / delete / send-test / view-deliveries /
 * regenerate-secret actions against the `/webhooks` backend.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Badge,
  Alert,
  AlertDescription,
  Spinner,
  ConfirmDialog,
  Modal,
  Label,
} from '@/components/ui';
import {
  Plus,
  Webhook as WebhookIcon,
  Trash2,
  Pencil,
  Send,
  ListChecks,
  KeyRound,
  AlertTriangle,
  Copy,
  Check,
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import {
  listWebhooks,
  deleteWebhook,
  sendTestWebhookEvent,
  regenerateWebhookSecret,
  WebhookListItem,
} from '@/services/webhookService';
import { WebhookFormModal } from './WebhookFormModal';
import { WebhookDeliveriesModal } from './WebhookDeliveriesModal';
import { useUserTimezone } from '@/hooks/useDateFormat';

interface WebhookListProps {
  organizationId: string;
}

function formatDate(date: Date | string | undefined, timeZone: string): string {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

const STATUS_CLASS: Record<WebhookListItem['status'], string> = {
  active: 'bg-status-success-bg text-status-success',
  inactive: 'bg-muted text-muted-foreground',
  disabled: 'bg-status-error-bg text-status-error',
};

export function WebhookList({ organizationId }: WebhookListProps) {
  const { success, error: showError } = useToast();
  const timeZone = useUserTimezone();

  const [webhooks, setWebhooks] = useState<WebhookListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form modal (create + edit)
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookListItem | null>(null);

  // Deliveries modal
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookListItem | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<WebhookListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Per-row action in flight (test)
  const [testingId, setTestingId] = useState<string | null>(null);

  // Regenerate secret
  const [regenTarget, setRegenTarget] = useState<WebhookListItem | null>(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenSecret, setRegenSecret] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const fetchWebhooks = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError('');
    try {
      const response = await listWebhooks(organizationId);
      setWebhooks(response.webhooks || []);
    } catch (err: any) {
      console.error('Error fetching webhooks:', err);
      setError(err.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleSendTest = async (webhook: WebhookListItem) => {
    setTestingId(webhook.id);
    try {
      const result = await sendTestWebhookEvent(webhook.id, organizationId);
      if (result.success) {
        success(
          `Test event delivered${result.responseStatus ? ` (HTTP ${result.responseStatus})` : ''}`,
          webhook.name
        );
      } else {
        showError(result.error || 'Endpoint did not accept the test event', webhook.name);
      }
      fetchWebhooks();
    } catch (err: any) {
      showError(err.message || 'Failed to send test event', webhook.name);
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWebhook(deleteTarget.id, organizationId);
      success('Webhook deleted');
      setDeleteTarget(null);
      fetchWebhooks();
    } catch (err: any) {
      showError(err.message || 'Failed to delete webhook');
    } finally {
      setDeleting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regenTarget) return;
    setRegenLoading(true);
    try {
      const response = await regenerateWebhookSecret(regenTarget.id, organizationId);
      setRegenSecret(response.secret);
    } catch (err: any) {
      showError(err.message || 'Failed to regenerate secret');
      setRegenTarget(null);
    } finally {
      setRegenLoading(false);
    }
  };

  const closeRegen = () => {
    setRegenTarget(null);
    setRegenSecret(null);
    setSecretCopied(false);
  };

  const copySecret = async () => {
    if (!regenSecret) return;
    try {
      await navigator.clipboard.writeText(regenSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {webhooks.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <WebhookIcon className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-muted-foreground">No webhooks yet</p>
          <p className="text-sm text-muted-foreground/70 mb-4">
            Create a webhook to receive event notifications at your endpoint
          </p>
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Webhook
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((webhook) => (
            <div
              key={webhook.id}
              className="p-4 bg-card rounded border border-border"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-foreground truncate">{webhook.name}</span>
                    <Badge className={STATUS_CLASS[webhook.status]}>{webhook.status}</Badge>
                    <Badge variant="secondary" className="text-xs">
                      {webhook.events.length} event{webhook.events.length === 1 ? '' : 's'}
                    </Badge>
                    {webhook.failureCount > 0 && (
                      <Badge variant="outline" className="text-xs text-status-warning border-status-warning/40">
                        {webhook.failureCount} recent failure{webhook.failureCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <code className="text-sm text-muted-foreground/80 font-mono break-all">
                    {webhook.url}
                  </code>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground/70 flex-wrap">
                    <span>Last delivery: {formatDate(webhook.lastDeliveryAt, timeZone)}</span>
                    {webhook.lastDeliveryStatus != null && (
                      <span>HTTP {webhook.lastDeliveryStatus}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 mt-3 flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSendTest(webhook)}
                  disabled={testingId === webhook.id}
                >
                  {testingId === webhook.id ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <Send className="w-4 h-4 mr-1" />
                  )}
                  Test
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeliveriesFor(webhook)}
                >
                  <ListChecks className="w-4 h-4 mr-1" />
                  Deliveries
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(webhook);
                    setFormOpen(true);
                  }}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRegenSecret(null);
                    setRegenTarget(webhook);
                  }}
                >
                  <KeyRound className="w-4 h-4 mr-1" />
                  Secret
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(webhook)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}

          <Button
            variant="secondary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Webhook
          </Button>
        </div>
      )}

      {/* Create / Edit modal */}
      <WebhookFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        organizationId={organizationId}
        webhook={editing}
        onSuccess={fetchWebhooks}
      />

      {/* Deliveries modal */}
      <WebhookDeliveriesModal
        isOpen={!!deliveriesFor}
        onClose={() => setDeliveriesFor(null)}
        organizationId={organizationId}
        webhook={deliveriesFor}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Webhook"
        description={`Delete "${deleteTarget?.name}"? Your endpoint will stop receiving events immediately. This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleting}
      />

      {/* Regenerate secret */}
      <Modal
        isOpen={!!regenTarget}
        onClose={closeRegen}
        title={regenSecret ? 'New Signing Secret' : 'Regenerate Secret'}
        description={
          regenSecret
            ? undefined
            : `Generate a new signing secret for "${regenTarget?.name}"? The current secret stops working immediately — update your endpoint.`
        }
        size="md"
        footer={
          regenSecret ? (
            <Button onClick={closeRegen}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeRegen} disabled={regenLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRegenerate} disabled={regenLoading}>
                {regenLoading && <Spinner size="sm" className="mr-2" />}
                Regenerate
              </Button>
            </>
          )
        }
      >
        {regenSecret ? (
          <div className="space-y-4">
            <Alert className="border-status-warning bg-status-warning-bg">
              <AlertTriangle className="h-4 w-4 text-status-warning" />
              <AlertDescription className="text-status-warning">
                <strong>Important:</strong> Copy this secret now. You won't be able to see it again!
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>Signing Secret</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all border">
                  {regenSecret}
                </div>
                <Button variant="outline" size="icon" onClick={copySecret} className="shrink-0">
                  {secretCopied ? (
                    <Check className="w-4 h-4 text-status-success" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-status-warning-bg/30 border border-status-warning/30 rounded-lg p-4 text-sm text-muted-foreground">
            Any receiver verifying signatures with the old secret will reject deliveries until you
            update it with the new one.
          </div>
        )}
      </Modal>
    </>
  );
}

export default WebhookList;
